import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Wifi as WifiIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Shield as ShieldIcon,
  Public as GlobeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as HiddenIcon,
  BoltOutlined as PowerIcon,
  Router as RouterIcon,
} from '@mui/icons-material';
import { decodeWiFiIE, getIESummaryChips } from '../../utils/wifiIEDecoder';

interface WiFiIEPanelProps {
  informationElements: string;
  compact?: boolean;
  isDarkMode?: boolean;
}

const ThreatBadge: React.FC<{ score: number; isDark: boolean }> = ({ score, isDark }) => {
  let color: string;
  let label: string;
  if (score >= 60) { color = '#f44336'; label = 'HIGH'; }
  else if (score >= 30) { color = '#ff9800'; label = 'MEDIUM'; }
  else if (score > 0) { color = '#ffc107'; label = 'LOW'; }
  else { color = '#4caf50'; label = 'CLEAR'; }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ShieldIcon sx={{ fontSize: 16, color }} />
      <Typography variant="caption" fontWeight="bold" sx={{ color }}>
        {label} ({score})
      </Typography>
      <LinearProgress
        variant="determinate"
        value={score}
        sx={{
          width: 60,
          height: 6,
          borderRadius: 3,
          bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          '& .MuiLinearProgress-bar': {
            bgcolor: color,
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode; isDark: boolean }> = ({
  label, value, icon, isDark,
}) => (
  <TableRow sx={{ '& td': { borderBottom: `1px solid ${isDark ? '#1e293b' : '#f0f0f0'}`, py: 0.5 } }}>
    <TableCell sx={{ width: 140, fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#666', verticalAlign: 'middle' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon}
        {label}
      </Box>
    </TableCell>
    <TableCell sx={{ fontSize: '0.75rem' }}>{value}</TableCell>
  </TableRow>
);

const WiFiIEPanel: React.FC<WiFiIEPanelProps> = ({ informationElements, compact = false, isDarkMode = true }) => {
  const [showRaw, setShowRaw] = useState(false);
  const decoded = useMemo(() => decodeWiFiIE(informationElements), [informationElements]);

  if (!informationElements) return null;

  const chips = getIESummaryChips(decoded);

  if (compact) {
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
        {chips.map((chip, i) => (
          <Chip key={i} label={chip.label} color={chip.color} size="small" sx={{ height: 20, fontSize: '0.6rem' }} />
        ))}
      </Stack>
    );
  }

  return (
    <Box sx={{
      bgcolor: isDarkMode ? '#0a0f1a' : '#fafafa',
      border: `1px solid ${isDarkMode ? '#1e293b' : '#e0e0e0'}`,
      borderRadius: 1.5,
      overflow: 'hidden',
    }}>
      {/* Header with threat score */}
      <Box sx={{
        px: 1.5, py: 1,
        bgcolor: isDarkMode ? '#0d1524' : '#f5f5f5',
        borderBottom: `1px solid ${isDarkMode ? '#1e293b' : '#e0e0e0'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <WifiIcon sx={{ fontSize: 16, color: isDarkMode ? '#00bcd4' : '#0097a7' }} />
          <Typography variant="caption" fontWeight="bold" sx={{ color: isDarkMode ? '#e2e8f0' : '#333' }}>
            Signal Intelligence
          </Typography>
          <ThreatBadge score={decoded.threats.threatScore} isDark={isDarkMode} />
        </Stack>
        <Tooltip title={showRaw ? 'Hide raw IEs' : 'Show raw IEs'}>
          <IconButton size="small" onClick={() => setShowRaw(!showRaw)} sx={{ color: isDarkMode ? '#64748b' : '#999' }}>
            {showRaw ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary chips */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
          {chips.map((chip, i) => (
            <Chip key={i} label={chip.label} color={chip.color} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem' }} />
          ))}
        </Stack>
      </Box>

      {/* Threat flags */}
      {decoded.threats.threatFlags.length > 0 && (
        <Box sx={{
          mx: 1.5, mb: 1, p: 1,
          bgcolor: isDarkMode ? 'rgba(244,67,54,0.08)' : 'rgba(244,67,54,0.05)',
          border: '1px solid',
          borderColor: isDarkMode ? 'rgba(244,67,54,0.25)' : 'rgba(244,67,54,0.2)',
          borderRadius: 1,
        }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <WarningIcon sx={{ fontSize: 14, color: '#f44336' }} />
            <Typography variant="caption" fontWeight="bold" sx={{ color: '#f44336' }}>
              DFP Threat Indicators
            </Typography>
          </Stack>
          {decoded.threats.threatFlags.map((flag, i) => (
            <Typography key={i} variant="caption" display="block" sx={{ color: isDarkMode ? '#fca5a5' : '#d32f2f', pl: 2.5, fontSize: '0.65rem' }}>
              {flag}
            </Typography>
          ))}
        </Box>
      )}

      {/* Detail grid */}
      <Box sx={{ px: 1 }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { border: 'none', px: 1 } }}>
          <TableBody>
            {decoded.security.protocol !== 'Unknown' && (
              <DetailRow
                label="Security"
                icon={<SecurityIcon sx={{ fontSize: 12 }} />}
                isDark={isDarkMode}
                value={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" fontWeight="bold">{decoded.security.protocol}</Typography>
                    {decoded.security.pmfRequired && <Chip label="PMF" size="small" color="success" sx={{ height: 16, fontSize: '0.55rem' }} />}
                  </Stack>
                }
              />
            )}
            {decoded.security.pairwiseCiphers.length > 0 && (
              <DetailRow label="Ciphers" isDark={isDarkMode} value={decoded.security.pairwiseCiphers.join(', ')} />
            )}
            {decoded.security.akmSuites.length > 0 && (
              <DetailRow label="Auth" isDark={isDarkMode} value={decoded.security.akmSuites.join(', ')} />
            )}
            {decoded.channel && (
              <DetailRow label="Channel" icon={<RouterIcon sx={{ fontSize: 12 }} />} isDark={isDarkMode} value={`Channel ${decoded.channel}`} />
            )}
            <DetailRow
              label="WiFi Standard"
              icon={<WifiIcon sx={{ fontSize: 12 }} />}
              isDark={isDarkMode}
              value={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" fontWeight="bold">{decoded.capabilities.wifiGeneration}</Typography>
                  <Typography variant="caption" color="text.secondary">({decoded.capabilities.channelWidth})</Typography>
                </Stack>
              }
            />
            {decoded.countryCode && (
              <DetailRow
                label="Country"
                icon={<GlobeIcon sx={{ fontSize: 12 }} />}
                isDark={isDarkMode}
                value={
                  <Typography variant="caption" fontWeight="bold" sx={{ color: decoded.threats.isForeignCountry ? '#f44336' : 'inherit' }}>
                    {decoded.countryCode} {decoded.threats.isForeignCountry && '(FOREIGN)'}
                  </Typography>
                }
              />
            )}
            {decoded.transmitPowerDbm !== null && (
              <DetailRow
                label="TX Power"
                icon={<PowerIcon sx={{ fontSize: 12 }} />}
                isDark={isDarkMode}
                value={
                  <Typography variant="caption" fontWeight="bold" sx={{ color: decoded.threats.highTransmitPower ? '#ff9800' : 'inherit' }}>
                    {decoded.transmitPowerDbm} dBm {decoded.threats.highTransmitPower && '(HIGH)'}
                  </Typography>
                }
              />
            )}
            <DetailRow
              label="SSID Visibility"
              icon={decoded.isHiddenSSID ? <HiddenIcon sx={{ fontSize: 12 }} /> : <VisibilityIcon sx={{ fontSize: 12 }} />}
              isDark={isDarkMode}
              value={decoded.isHiddenSSID ? <Typography variant="caption" color="warning.main" fontWeight="bold">Hidden</Typography> : 'Broadcast'}
            />
            {decoded.stationCount !== null && (
              <DetailRow label="Connected Stations" isDark={isDarkMode} value={decoded.stationCount} />
            )}
            {decoded.channelUtilization !== null && (
              <DetailRow label="Channel Load" isDark={isDarkMode} value={`${decoded.channelUtilization}%`} />
            )}
            {decoded.vendorInfo.length > 0 && (
              <DetailRow
                label="Vendor IEs"
                isDark={isDarkMode}
                value={
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.3 }}>
                    {Array.from(new Set(decoded.vendorInfo.map(v => v.name))).map((name: any, i) => (
                      <Chip key={i} label={name as string} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.55rem' }} />
                    ))}
                  </Stack>
                }
              />
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Raw IE data (collapsible) */}
      <Collapse in={showRaw}>
        <Divider />
        <Box sx={{ p: 1.5, maxHeight: 200, overflow: 'auto' }}>
          <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5, color: isDarkMode ? '#64748b' : '#999' }}>
            Raw Information Elements ({decoded.elements.length})
          </Typography>
          {decoded.elements.map((ie, i) => (
            <Box key={i} sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: isDarkMode ? '#94a3b8' : '#666' }}>
                <strong>IE {ie.id}</strong> ({ie.name}): {ie.rawHex.length > 60 ? ie.rawHex.substring(0, 60) + '...' : ie.rawHex}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default WiFiIEPanel;
